import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Box,
    Collapse,
    Backdrop,
    useTheme,
} from '@mui/material';
// import { useAppTheme } from '@contexts/ThemeContext';
import {
    Dashboard,
    Notifications,
    Report,
    BusinessCenter,
    ExpandLess,
    ExpandMore,
    School,
    Work,
    CalendarToday,
    BeachAccess,
    People,
    Feed,
    EventNote,
    CalendarMonth,
    AdminPanelSettings,
    AccountBalanceWallet,
    Add,
    Assignment,
    Category,
    Campaign,
    EmojiEvents,
    Inventory,
    Web,
    VerifiedUser,
    Domain,
    AccountTree,
    SupervisorAccount,
    Policy,
    ManageAccounts,
} from '@mui/icons-material';
import { FlagTriangleRight } from 'lucide-react';
import { useAuth } from '@store/authStore';
import { UserRole } from '@config/roles';
import { hasRoutePermission } from '@config/routes';
import { usePermissions } from '@contexts/PermissionContext';

interface SidebarProps {
    open: boolean;
    onClose: () => void;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    darkMode?: boolean;
}


interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    hasSubmenu?: boolean;
    submenu?: MenuItem[];
}

/**
 * SIDEBAR MENU CONFIGURATION
 * Define menu structure, order, icons, and labels HERE
 * Permissions are auto-checked from routes.ts
 */

/**
 * Daily Plans & Reports. One definition, spread into every menu that gets it.
 * Declared before the menu arrays because they reference it at module load.
 *
 * The parent isn't navigable — it just expands — and the Sidebar auto-filters the
 * children by hasRoutePermission and hides the parent entirely when a user can
 * reach none of them. So an Employee who is not a manager still sees the item but
 * gets an empty board (the API scopes them to their own reportees, of which they
 * have none), while an Employee who IS a team lead sees their team.
 */
const planReportsMenu: MenuItem = {
    text: 'Daily Plans & Reports',
    icon: <Report />,
    path: '/hr/plan-reports',
    hasSubmenu: true,
    submenu: [
        { text: 'Daily Board', icon: <CalendarToday />, path: '/hr/plan-reports/board' },
        { text: 'Period Report', icon: <CalendarMonth />, path: '/hr/plan-reports/period' },
        { text: 'Slippage & Blockers', icon: <Report />, path: '/hr/plan-reports/slippage' },
        { text: 'Analytics', icon: <BusinessCenter />, path: '/hr/plan-reports/analytics' },
    ],
};

// Program Manager Sidebar (formerly Admin)
const programManagerMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Role Center', icon: <AdminPanelSettings />, path: '/role-decider' },
    { text: 'Organization Feed', icon: <Campaign />, path: '/organization-feed' },
    { text: 'My Work', icon: <Assignment />, path: '/my-work' },
    { text: 'Daily Log', icon: <Work />, path: '/daily-log' },
    planReportsMenu,
    {
        text: 'Management Index',
        icon: <BusinessCenter />,
        path: '/management-index',
        hasSubmenu: true,
        submenu: [
            { text: 'Sessions', icon: <School />, path: '/sessions' },
            { text: 'Workshops', icon: <Work />, path: '/workshops' },
        ],
    },
    { text: 'Reports', icon: <Report />, path: '/reports' },
    { text: 'Notifications', icon: <Notifications />, path: '/notifications' },
    { text: 'Leave Portal', icon: <BeachAccess />, path: '/leave-portal' },
    { text: 'Holidays', icon: <EventNote />, path: '/holiday-calendar-view' },
    { text: 'Wall of Fame', icon: <EmojiEvents />, path: '/wall-of-fame' },
];

// HR Sidebar
const hrMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Role Center', icon: <AdminPanelSettings />, path: '/role-decider' },
    { text: 'Organization Feed', icon: <Campaign />, path: '/organization-feed' },
    { text: 'Pay Roll', icon: <AccountBalanceWallet />, path: '/payroll' },
    { text: 'Salary Review', icon: <AccountBalanceWallet />, path: '/hr/salary-review' },
    { text: 'My Work', icon: <Assignment />, path: '/my-work' },
    { text: 'Daily Log', icon: <Work />, path: '/daily-log' },
    { text: 'Employees', icon: <People />, path: '/employees' },
    { text: 'Daily Tracker', icon: <CalendarToday />, path: '/daily-tracker' },
    { text: 'Daily Updates Feed', icon: <Feed />, path: '/daily-updates-feed' },
    planReportsMenu,
    { text: 'Leaves', icon: <BeachAccess />, path: '/leaves' },
    { text: 'Leave Portal', icon: <BeachAccess />, path: '/leave-portal' },
    { text: 'Holidays', icon: <EventNote />, path: '/holiday-calendar-view' },
    { text: 'Holiday Management', icon: <CalendarMonth />, path: '/holiday-management' },
    { text: 'Leave Policy', icon: <Policy />, path: '/leave-policy-settings' },
    { text: 'Wall of Fame', icon: <EmojiEvents />, path: '/hr/recognitions' },
    {
        text: 'Products',
        icon: <Inventory />,
        path: '/products',
        hasSubmenu: true,
        submenu: [
            { text: 'Magnetix', icon: <Web />, path: '/products/magnetix' },
        ],
    },
];

// Employee Sidebar
const employeeMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Role Center', icon: <AdminPanelSettings />, path: '/role-decider' },
    { text: 'Organization Feed', icon: <Campaign />, path: '/organization-feed' },
    { text: 'My Work', icon: <Assignment />, path: '/my-work' },
    { text: 'Daily Log', icon: <Work />, path: '/daily-log' },
    { text: 'Team Update', icon: <People />, path: '/team-attendance' },
    planReportsMenu,
    { text: 'Leave Portal', icon: <BeachAccess />, path: '/leave-portal' },
    { text: 'Resources', icon: <Feed />, path: '/employee-resources' },
    { text: 'Holidays', icon: <EventNote />, path: '/holiday-calendar-view' },
    { text: 'Wall of Fame', icon: <EmojiEvents />, path: '/wall-of-fame' },
];

// Admin Sidebar (formerly Super Admin)
const adminMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Role Center', icon: <AdminPanelSettings />, path: '/role-decider' },
    { text: 'Organization Feed', icon: <Campaign />, path: '/organization-feed' },
    { text: 'My Work', icon: <Assignment />, path: '/my-work' },
    { text: 'Daily Log', icon: <Work />, path: '/daily-log' },
    { text: 'Manage Users', icon: <People />, path: '/manage-users' },
    { text: 'Daily Updates Feed', icon: <Feed />, path: '/daily-updates' },
    planReportsMenu,
    { text: 'Leave Portal', icon: <BeachAccess />, path: '/leave-portal' },
    { text: 'Holidays', icon: <EventNote />, path: '/holiday-calendar-view' },
    { text: 'Leave Policy', icon: <Policy />, path: '/leave-policy-settings' },
    { text: 'Wall of Fame', icon: <EmojiEvents />, path: '/wall-of-fame' },
    {
        text: 'Products',
        icon: <Inventory />,
        path: '/products',
        hasSubmenu: true,
        submenu: [
            { text: 'Magnetix', icon: <Web />, path: '/products/magnetix' },
        ],
    },
];

// Assessment Creator Sidebar
const assessmentCreatorMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Role Center', icon: <AdminPanelSettings />, path: '/role-decider' },
    { text: 'Organization Feed', icon: <Campaign />, path: '/organization-feed' },
    { text: 'My Work', icon: <Assignment />, path: '/my-work' },
    { text: 'Manage Assessments', icon: <Assignment />, path: '/manage-assessments' },
    { text: 'Assessment Eligibility', icon: <VerifiedUser />, path: '/manage-assessment-eligibility' },
    { text: 'Assessment Domains', icon: <Domain />, path: '/manage-assessment-domains' },
    { text: 'Manage Admins', icon: <ManageAccounts />, path: '/manage-organizational-admins' },
    { text: 'Reporting Managers', icon: <AccountTree />, path: '/assessment-creator/360-feedback/reporting-managers' },
    { text: 'Reportee Management', icon: <SupervisorAccount />, path: '/assessment-creator/180-feedback/reportee-management' },
    { text: 'Create Assessment', icon: <Add />, path: '/create-assessment' },
    { text: 'Dimensions & Modules', icon: <Category />, path: '/manage-dimensions-modules' },
    { text: 'Users', icon: <People />, path: '/assessment-users' },
    { text: 'Leave Portal', icon: <BeachAccess />, path: '/leave-portal' },
    { text: 'Wall of Fame', icon: <EmojiEvents />, path: '/wall-of-fame' },
];

// Default Sidebar (for roles without specific menu items)
const defaultMenuItems: MenuItem[] = [];

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, isExpanded, onToggleExpanded, darkMode = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { user } = useAuth();
    const { hasAssignedTasks } = usePermissions();
    const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>({
        'Management Index': true // Default open for Management Index
    });

    /**
     * GET SIDEBAR FOR USER'S ROLE
     * Permissions are auto-verified from routes.ts
     * Returns default menu (Profile Settings only) for roles without specific menu items
     */
    const getMenuItemsForRole = (role: string | undefined): MenuItem[] => {
        switch (role) {
            case UserRole.HR:
                return hrMenuItems;
            case UserRole.EMPLOYEE:
                // Add Management Index only if employee has assigned tasks
                const baseEmployeeMenu = [...employeeMenuItems];
                if (hasAssignedTasks) {
                    // Insert Management Index before the last item (Profile Settings)
                    baseEmployeeMenu.splice(-1, 0, {
                        text: 'Management Index',
                        icon: <BusinessCenter />,
                        path: '/management-index',
                        hasSubmenu: true,
                        submenu: [
                            { text: 'Workshops', icon: <Work />, path: '/workshops' },
                        ],
                    });
                }
                return baseEmployeeMenu;
            case UserRole.ADMIN:
                return adminMenuItems;
            case UserRole.PROGRAM_MANAGER:
                return programManagerMenuItems;
            case UserRole.ASSESSMENT_CREATOR:
                return assessmentCreatorMenuItems;
            default:
                return defaultMenuItems;
        }
    };

    const menuItems = getMenuItemsForRole(user?.role);

    /**
     * SAFETY LAYER: Verify permissions from routes.ts
     * This ensures menu items match the actual route permissions
     * For items with submenus, we check if user has access to ANY submenu item
     * Additionally, filter out "Role Center" menu item if user is not a super admin
     */
    const filteredMenuItems = menuItems
        .map((item) => {
            // Special handling for "Role Center" menu item
            if (item.text === 'Role Center') {
                // Only show if user is a super admin (is_super_admin === 1)
                if (user?.is_super_admin === 1) {
                    return item;
                }
                return null;
            }

            // If item has submenu, filter the submenu items
            if (item.hasSubmenu && item.submenu) {
                const filteredSubmenu = item.submenu.filter((subItem) =>
                    hasRoutePermission(user?.role, subItem.path)
                );

                // Only show parent if user has access to at least one submenu item
                if (filteredSubmenu.length > 0) {
                    return { ...item, submenu: filteredSubmenu };
                }
                return null;
            }

            // For regular items, check permission
            if (hasRoutePermission(user?.role, item.path)) {
                return item;
            }

            return null;
        })
        .filter((item): item is MenuItem => item !== null);

    const collapsedWidth = 64;
    const expandedWidth = 240; // Increased from 200 to 240 for better spacing
    const currentWidth = isExpanded ? expandedWidth : collapsedWidth;

    const handleSubmenuToggle = (menuText: string) => {
        setOpenSubmenus(prev => ({
            ...prev,
            [menuText]: !prev[menuText]
        }));
    };

    const handleItemClick = (path: string, hasSubmenu?: boolean, menuText?: string) => {
        console.log('Sidebar: Clicked on', path, 'hasSubmenu:', hasSubmenu);

        if (hasSubmenu && menuText) {
            handleSubmenuToggle(menuText);
            return;
        }

        navigate(path);
    };

    // Removed hover functionality - sidebar only expands via hamburger button

    return (
        <>
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true,
                    disableScrollLock: false,
                }}
                PaperProps={{
                    sx: {
                        width: expandedWidth,
                        maxWidth: '75vw',
                        boxSizing: 'border-box',
                        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                        color: theme.palette.text.primary,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                            : '0 8px 32px rgba(0, 0, 0, 0.1)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        zIndex: 1300,
                        marginTop: { xs: '56px', sm: '64px' }, // Push content below header
                        height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, // Adjust height
                    }
                }}
                sx={{
                    display: { xs: 'block', md: 'none' }, // Show on mobile and tablet
                    zIndex: 1300,
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1299,
                    },
                }}
            >
                <List sx={{ px: 2, py: 3 }}>
                    {filteredMenuItems.map((item) => (
                        <React.Fragment key={item.text}>
                            <ListItem disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent main content click
                                        handleItemClick(item.path, item.hasSubmenu, item.text);
                                    }}
                                    selected={location.pathname === item.path || (item.hasSubmenu && item.submenu?.some(sub => location.pathname === sub.path))}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(79, 70, 229, 0.2)',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        color: theme.palette.text.primary,
                                        minWidth: 48,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1.8rem'
                                        },
                                        position: 'relative'
                                    }}>
                                        {item.icon}
                                        {item.text === 'Daily Log' && user?.flag_daily_update === 0 && (
                                            <FlagTriangleRight
                                                size={14}
                                                fill="#FFC107"
                                                style={{
                                                    position: 'absolute',
                                                    top: -4,
                                                    right: -4,
                                                    color: '#FFC107',
                                                }}
                                            />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        sx={{
                                            '& .MuiListItemText-primary': {
                                                fontSize: '0.9rem',
                                                fontWeight: 500,
                                            },
                                            mr: 3,
                                        }}
                                    />
                                    {item.hasSubmenu && (
                                        openSubmenus[item.text] ? <ExpandLess /> : <ExpandMore />
                                    )}
                                </ListItemButton>
                            </ListItem>
                            {item.hasSubmenu && (
                                <Collapse in={openSubmenus[item.text]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.submenu?.map((subItem) => (
                                            <ListItem key={subItem.text} disablePadding sx={{ pl: 4, mb: 0.5 }}>
                                                <ListItemButton
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent main content click
                                                        handleItemClick(subItem.path);
                                                    }}
                                                    selected={location.pathname === subItem.path}
                                                    sx={{
                                                        borderRadius: 2,
                                                        py: 1.2,
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(79, 70, 229, 0.15)',
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{
                                                        color: theme.palette.text.primary,
                                                        minWidth: 40,
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '1.5rem'
                                                        }
                                                    }}>
                                                        {subItem.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={subItem.text}
                                                        sx={{
                                                            '& .MuiListItemText-primary': {
                                                                fontSize: '0.85rem',
                                                                fontWeight: 400,
                                                            },
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Drawer>

            {/* Desktop Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' }, // Hide on mobile and tablet
                    width: currentWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: currentWidth,
                        boxSizing: 'border-box',
                        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                        color: theme.palette.text.primary,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        borderTop: 'none', // Remove top border
                        borderLeft: 'none', // Remove left border
                        borderBottom: 'none', // Remove bottom border
                        boxShadow: theme.palette.mode === 'dark'
                            ? '2px 0 8px rgba(0, 0, 0, 0.4)'
                            : '2px 0 8px rgba(0, 0, 0, 0.1)',
                        position: 'fixed',
                        top: { xs: '56px', sm: '64px' }, // Same as header height
                        height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, // Match header height
                        left: 0,
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        zIndex: 1200, // Above content but below navbar
                    },
                }}
            >
                <Box
                    sx={{
                        width: isExpanded ? expandedWidth : collapsedWidth,
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        height: '100%',
                    }}
                >
                    <List sx={{ px: 0.5, py: 1.5 }}>
                        {filteredMenuItems.map((item) => {
                            const isSelected = location.pathname === item.path || (item.hasSubmenu && item.submenu?.some(sub => location.pathname === sub.path));
                            const showText = isExpanded;

                            return (
                                <React.Fragment key={item.text}>
                                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                                        <Tooltip
                                            title={!showText ? item.text : ''}
                                            placement="right"
                                            arrow
                                            disableHoverListener={showText}
                                        >
                                            <ListItemButton
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent main content click
                                                    handleItemClick(item.path, item.hasSubmenu, item.text);
                                                }}
                                                selected={isSelected}
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.2,
                                                    px: 1,
                                                    minHeight: 44,
                                                    justifyContent: showText ? 'flex-start' : 'center',
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'rgba(79, 70, 229, 0.2)',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon sx={{
                                                    color: theme.palette.text.primary,
                                                    minWidth: showText ? 44 : 'auto',
                                                    justifyContent: 'center',
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '1.8rem'
                                                    },
                                                    position: 'relative'
                                                }}>
                                                    {item.icon}
                                                    {item.text === 'Daily Log' && user?.flag_daily_update === 0 && (
                                                        <FlagTriangleRight
                                                            size={14}
                                                            fill="#FFC107"
                                                            style={{
                                                                position: 'absolute',
                                                                top: -4,
                                                                right: -4,
                                                                color: '#FFC107',
                                                            }}
                                                        />
                                                    )}
                                                </ListItemIcon>
                                                {showText && (
                                                    <>
                                                        <ListItemText
                                                            primary={item.text}
                                                            sx={{
                                                                ml: 0.5,
                                                                mr: 3,
                                                                '& .MuiListItemText-primary': {
                                                                    fontSize: '0.85rem',
                                                                    fontWeight: 500,
                                                                    whiteSpace: 'nowrap',
                                                                },
                                                            }}
                                                        />
                                                        {item.hasSubmenu && (
                                                            openSubmenus[item.text] ? <ExpandLess /> : <ExpandMore />
                                                        )}
                                                    </>
                                                )}
                                            </ListItemButton>
                                        </Tooltip>
                                    </ListItem>
                                    {item.hasSubmenu && showText && (
                                        <Collapse in={openSubmenus[item.text]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {item.submenu?.map((subItem) => (
                                                    <ListItem key={subItem.text} disablePadding sx={{ pl: 2, mb: 0.25 }}>
                                                        <Tooltip
                                                            title={!showText ? subItem.text : ''}
                                                            placement="right"
                                                            arrow
                                                            disableHoverListener={showText}
                                                        >
                                                            <ListItemButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // Prevent main content click
                                                                    handleItemClick(subItem.path);
                                                                }}
                                                                selected={location.pathname === subItem.path}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    py: 1,
                                                                    px: 1,
                                                                    minHeight: 40,
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: 'rgba(79, 70, 229, 0.15)',
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{
                                                                    color: theme.palette.text.primary,
                                                                    minWidth: 36,
                                                                    justifyContent: 'center',
                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: '1.5rem'
                                                                    }
                                                                }}>
                                                                    {subItem.icon}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={subItem.text}
                                                                    sx={{
                                                                        ml: 0.5,
                                                                        '& .MuiListItemText-primary': {
                                                                            fontSize: '0.8rem',
                                                                            fontWeight: 400,
                                                                            whiteSpace: 'nowrap',
                                                                        },
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </Tooltip>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};